import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { PrecioProveedorState, SaveBulkRequest } from '../types';
import { PrecioProveedorService } from '../services/precioProveedorService';

interface PrecioProveedorActions {
  fetchByProveedor: (idProveedor: number) => Promise<void>;
  fetchByProveedorAndTipo: (idProveedor: number, tipoProceso: string, tipoOperacion: string) => Promise<void>;
  saveBulk: (request: SaveBulkRequest) => Promise<boolean>;
  deletePrecio: (id: number) => Promise<boolean>;
  clearError: () => void;
  reset: () => void;
}

type PrecioProveedorStore = PrecioProveedorState & PrecioProveedorActions;

const initialState: PrecioProveedorState = {
  precios: [],
  loading: false,
  error: null,
};

export const usePrecioProveedorStore = create<PrecioProveedorStore>()(
  devtools(
    (set) => ({
      ...initialState,

      fetchByProveedor: async (idProveedor: number) => {
        set({ loading: true, error: null });
        try {
          const precios = await PrecioProveedorService.getByProveedor(idProveedor);
          set({ precios, loading: false });
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Error al cargar precios',
            loading: false,
          });
        }
      },

      fetchByProveedorAndTipo: async (idProveedor: number, tipoProceso: string, tipoOperacion: string) => {
        set({ loading: true, error: null });
        try {
          const precios = await PrecioProveedorService.getByProveedorAndTipo(
            idProveedor, tipoProceso, tipoOperacion
          );
          set({ precios, loading: false });
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Error al cargar precios',
            loading: false,
          });
        }
      },

      saveBulk: async (request: SaveBulkRequest) => {
        set({ loading: true, error: null });
        try {
          await PrecioProveedorService.saveBulk(request);
          set({ loading: false });
          return true;
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Error al guardar precios',
            loading: false,
          });
          return false;
        }
      },

      deletePrecio: async (id: number) => {
        set({ loading: true, error: null });
        try {
          await PrecioProveedorService.delete(id);
          set({ loading: false });
          return true;
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Error al eliminar precio',
            loading: false,
          });
          return false;
        }
      },

      clearError: () => set({ error: null }),
      reset: () => set(initialState),
    }),
    { name: 'precio-proveedor-store' }
  )
);
